import React from 'react';
import {
  chipStyle,
  chipWrapStyle,
  rowMetaStyle,
  rowTitleStyle,
  summaryCardStyle,
} from './dashboardStyles';

const TRIAGE_ORDER = [
  'scope_excluded_but_assigned',
  'assign_needed',
  'off_candidate',
  'sales_structure',
  'operator_keep',
  'scope_excluded',
  'danger',
  'review',
];

export function statusPillStyle(status) {
  const palette = {
    ok: { border: '#16a34a', background: 'rgba(22,163,74,0.12)', color: '#15803d' },
    info: { border: '#2563eb', background: 'rgba(37,99,235,0.12)', color: '#1d4ed8' },
    warning: { border: '#d97706', background: 'rgba(217,119,6,0.12)', color: '#b45309' },
    danger: { border: '#dc2626', background: 'rgba(220,38,38,0.12)', color: '#b91c1c' },
    issue: { border: '#dc2626', background: 'rgba(220,38,38,0.12)', color: '#b91c1c' },
  };
  const color = palette[status] || palette.issue;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 6,
    border: `1px solid ${color.border}`,
    background: color.background,
    color: color.color,
    padding: '2px 7px',
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  };
}

function actionButtonStyle(disabled) {
  return {
    border: '1px solid #dc2626',
    borderRadius: 7,
    background: disabled ? 'rgba(220,38,38,0.08)' : 'rgba(220,38,38,0.12)',
    color: '#b91c1c',
    padding: '5px 9px',
    fontSize: 12,
    fontWeight: 900,
    cursor: disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap',
    opacity: disabled ? 0.6 : 1,
  };
}

export function groupRuntimeIssues(issues) {
  const grouped = new Map();
  for (const issue of issues || []) {
    const type = issue.triage?.type || 'review';
    if (!grouped.has(type)) {
      grouped.set(type, {
        type,
        label: issue.triage?.label || '검토 필요',
        status: issue.triage?.status || 'warning',
        detail: issue.triage?.detail || '',
        nextAction: issue.triage?.nextAction || '',
        items: [],
      });
    }
    grouped.get(type).items.push(issue);
  }
  return Array.from(grouped.values()).sort((a, b) => {
    const left = TRIAGE_ORDER.indexOf(a.type);
    const right = TRIAGE_ORDER.indexOf(b.type);
    return (left < 0 ? 99 : left) - (right < 0 ? 99 : right);
  });
}

function RuntimeIssueItem({ item, savingKey, onDisableCompanyModule }) {
  const activePermissions = (item.activePermissions || [])
    .map((permission) => `${permission.label || permission.code} ${permission.holderCount}명`)
    .join(' · ');
  const key = `${item.companyCode}:${item.moduleCode}`;
  // 2026-10-19: 끄기는 테넌트 관리 › 회사 탭에서만 — 손잡이를 안 주면 버튼을 숨긴다(점검 화면은 읽기 전용)
  const canDisable = Boolean(onDisableCompanyModule) && item.triage?.type === 'off_candidate' && item.companyEnabled;
  const saving = savingKey === key;

  return (
    <div
      style={{
        display: 'grid',
        gap: 6,
        border: '1px solid var(--border)',
        borderRadius: 7,
        padding: '8px 9px',
        background: 'var(--bg-card)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={rowTitleStyle}>{item.companyName || item.companyCode} / {item.moduleName || item.moduleCode}</div>
          <div style={rowMetaStyle}>{item.check?.label || item.check?.type}</div>
        </div>
        <span style={statusPillStyle(item.triage?.status || 'warning')}>{item.triage?.label || '검토 필요'}</span>
      </div>
      <div style={chipWrapStyle}>
        <span style={chipStyle}>원장 {item.companyEnabled ? 'ON' : 'OFF'}</span>
        <span style={chipStyle}>권한 {item.permissionCount}</span>
        <span style={chipStyle}>직원 {item.assignedUserCount}</span>
      </div>
      {activePermissions ? <div style={rowMetaStyle}>권한: {activePermissions}</div> : null}
      {item.triage?.nextAction ? <div style={rowMetaStyle}>다음 행동: {item.triage.nextAction}</div> : null}
      {canDisable ? (
        <div>
          <button
            type="button"
            style={actionButtonStyle(saving)}
            disabled={saving}
            onClick={() => onDisableCompanyModule(item)}
          >
            {saving ? '저장 중' : '회사 원장 끄기'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function CompanyModuleRuntimeGroups({
  issues,
  savingKey,
  onDisableCompanyModule,
}) {
  if (!issues.length) {
    return (
      <div style={{ ...summaryCardStyle, borderColor: 'rgba(22,163,74,0.35)', background: 'rgba(22,163,74,0.08)' }}>
        <div style={rowTitleStyle}>회사별 기능 원장과 실제 직원 권한이 충돌하지 않습니다.</div>
        <div style={rowMetaStyle}>아직 권한을 자동 적용하지 않는 읽기 전용 점검 결과입니다.</div>
      </div>
    );
  }

  const groups = groupRuntimeIssues(issues);
  return (
    <div style={{ display: 'grid', gap: 9 }}>
      {groups.map((group) => (
        <div
          key={group.type}
          style={{
            display: 'grid',
            gap: 7,
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 10,
            background: 'rgba(148,163,184,0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={rowTitleStyle}>{group.label}</div>
              <div style={rowMetaStyle}>{group.detail}</div>
            </div>
            <span style={statusPillStyle(group.status)}>{group.items.length}건</span>
          </div>
          {group.nextAction ? <div style={rowMetaStyle}>기본 처리: {group.nextAction}</div> : null}
          <div style={{ display: 'grid', gap: 6 }}>
            {group.items.map((item) => (
              <RuntimeIssueItem
                key={`${item.companyCode}:${item.moduleCode}:${item.triage?.type}`}
                item={item}
                savingKey={savingKey}
                onDisableCompanyModule={onDisableCompanyModule}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
